import React, { useMemo, lazy, Suspense } from 'react';
import './App.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { DIALOG_GUTTER } from './theme/shellMetrics';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns as AdapterDateFnsV3 } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { es as esLocale } from 'date-fns/locale/es';
import { pt } from 'date-fns/locale/pt';
import { fr } from 'date-fns/locale/fr';
import { de } from 'date-fns/locale/de';
import { ja } from 'date-fns/locale/ja';
import { zhCN } from 'date-fns/locale/zh-CN';
import { ar } from 'date-fns/locale/ar';
import { ko } from 'date-fns/locale/ko';
import { enUS } from 'date-fns/locale/en-US';

import { useTranslation } from 'react-i18next';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { MainLayout } from './components';
import StepUpModal from './components/StepUpModal';
import { PRODUCT_NAME, FAVICON } from './branding';
import { AuthProvider, useAuth } from './auth/AuthContext';
import PortalBoundary from './auth/PortalBoundary';
import AdminProductBoundary from './auth/ProductBoundary';
import { workerRoute } from './auth/WorkerProductBoundary';
import { AuthGuardedPublicBoundary } from './auth/AuthGuardedPublic';
import { saveReturnTo, redirectToSignup } from './auth/authService';
import { getCurrentOrgId } from './api';
import { getCurrentRoleCode } from './api/client';
import { useRoleLanding } from './hooks/useRoleLanding';
import Dashboard from './pages/dashboard';

// Worker Portal
import WorkerLayout from './pages/WorkerPortal/WorkerLayout';
import AppPosterPage from './pages/AppPoster/AppPosterPage';
import WorkerDashboard from './pages/WorkerPortal/WorkerDashboard';
import WorkerSchedule from './pages/WorkerPortal/WorkerSchedule';
import WorkerTimeEntries from './pages/WorkerPortal/WorkerTimeEntries';
import WorkerLeave from './pages/WorkerPortal/WorkerLeave';
import WorkerRequests from './pages/WorkerPortal/WorkerRequests';
import WorkerAttendanceRequests from './pages/WorkerPortal/WorkerAttendanceRequests';
import WorkerApprovals from './pages/WorkerPortal/WorkerApprovals';
import WorkerAttendanceApprovals from './pages/WorkerPortal/WorkerAttendanceApprovals';
import WorkerRequestDetail from './pages/WorkerPortal/WorkerRequestDetail';
import WorkerTasks from './pages/WorkerPortal/WorkerTasks';
import WorkerSubmitRequest from './pages/WorkerPortal/WorkerSubmitRequest';
import WorkerProfile from './pages/WorkerPortal/WorkerProfile';
import AccountSecurity from './pages/Account/SecuritySettings';
import WorkerFaceTemplate from './pages/WorkerPortal/WorkerFaceTemplate';
import WorkerTeam from './pages/WorkerPortal/WorkerTeam';
import WorkerClockInReviews from './pages/WorkerPortal/WorkerClockInReviews';
import WorkerHolidays from './pages/WorkerPortal/WorkerHolidays';
import WorkerOrganization from './pages/WorkerPortal/WorkerOrganization';
import WorkerOrgChart from './pages/WorkerPortal/WorkerOrgChart';
import OrganizationProfile from './pages/Organization/OrganizationProfile';
import BranchesLocations from './pages/Organization/BranchesLocations';
import Departments from './pages/Organization/Departments';
import Positions from './pages/Organization/Positions';
import EmploymentTypes from './pages/Organization/EmploymentTypes';
import WorkerDocuments from './pages/Organization/WorkerDocuments';
import ActiveWorkers from './pages/Organization/ActiveWorkers';
import FormerWorkers from './pages/Organization/FormerWorkers';
import FaceTemplates from './pages/Organization/FaceTemplates';
import OrganizationChart from './pages/Organization/OrganizationChart';
import Events from './pages/Organization/Events';
import Announcements from './pages/Organization/Announcements';
import Roles from './pages/Processes/Roles';
import FormComponents from './pages/Processes/FormComponents';
import ProcessAnalytics from './pages/Processes/ProcessAnalytics';
import ProcessDesigner from './pages/Processes/ProcessDesigner';
import ProcessOverview from './pages/Processes/ProcessOverview';
import { PendingApprovals, AllRequests } from './pages/Processes/ProcessRequests';
import { InstanceDetail } from './pages/Processes/ProcessInstances';
import { Box, Typography } from '@mui/material';
import { SSOCallback, Onboarding } from './pages/Authentication';
import { SSOLanding } from './pages/Authentication';
import OrgGate from './pages/Authentication/OrgGate';
import AttendanceOverview from './pages/Attendance/AttendanceOverview';
import GlobalRules from './pages/Attendance/Rules/GlobalRules';
import ApprovalRouting from './pages/Attendance/Approvals/ApprovalRouting';
import DepartmentRules from './pages/Attendance/Rules/DepartmentRules';
import GroupRules from './pages/Attendance/Rules/GroupRules';
import Breaks from './pages/Attendance/Shifts/Breaks';
import Timetables from './pages/Attendance/Shifts/Timetables';
import ShiftCycles from './pages/Attendance/Shifts/ShiftCycles';
import WorkCalendar from './pages/Attendance/WorkCalendar/WorkCalendar';
import ScheduleView from './pages/Attendance/Schedules/ScheduleView';
import DepartmentSchedules from './pages/Attendance/Schedules/DepartmentSchedules';
import WorkerSchedules from './pages/Attendance/Schedules/WorkerSchedules';
import TemporarySchedules from './pages/Attendance/Schedules/TemporarySchedules';
import IntegrationsOverview from './pages/Integrations/IntegrationsOverview';
import IntegrationsAPI from './pages/Integrations/API';
import IntegrationsWebhooks from './pages/Integrations/Webhooks';
import IntegrationsConnectors from './pages/Integrations/Connectors';
import SystemOverview from './pages/System/SystemOverview';
import RolesPermissions from './pages/System/AccessControl/RolesPermissions';
import WorkerGroups from "./pages/Organization/WorkerGroups";
import AccessLogs from './pages/System/AuditLogs/AccessLogs';
import ActivityLogs from './pages/System/AuditLogs/ActivityLogs';
import SettingsLanding from './pages/System/Settings';
import SecuritySettings from './pages/System/Settings/Security';
import NotificationSettings from './pages/System/Settings/Notifications';
import DataExportSettings from './pages/System/Settings/DataExport';
import WorkersSettings from './pages/System/Settings/Workers';
import SsoSettings from './pages/System/Settings/SSO';
import SsoStrictMode from './pages/System/Settings/SSOStrictMode';
import Devices from './pages/System/Devices';
import SystemBilling from './pages/System/Billing/SystemBilling';
import SystemSupport from './pages/System/Support/SystemSupport';
import LeaveTypes from './pages/Attendance/LeaveManagement/LeaveTypes';
import LeavePolicies from './pages/Attendance/LeaveManagement/LeavePolicies';
import PublicHolidays from './pages/Attendance/LeaveManagement/PublicHolidays';
import LeaveBalances from './pages/Attendance/LeaveManagement/LeaveBalances';
import DepartmentGeofences from './pages/Attendance/Geofences/DepartmentGeofences';
import GroupGeofences from './pages/Attendance/Geofences/GroupGeofences';
import WorkerGeofences from './pages/Attendance/Geofences/WorkerGeofences';
import KioskDevices from './pages/Attendance/Kiosk';
import TimeEntries from './pages/Attendance/TimeEntries/TimeEntries';
import LeaveApprovals from './pages/Attendance/Approvals/LeaveApprovals';
import OvertimeApprovals from './pages/Attendance/Approvals/OvertimeApprovals';
import MissedClockInApprovals from './pages/Attendance/Approvals/MissedClockInApprovals';
import BusinessTripApprovals from './pages/Attendance/Approvals/BusinessTripApprovals';
import OffSiteWorkApprovals from './pages/Attendance/Approvals/OffSiteWorkApprovals';
import ScheduleChangeApprovals from './pages/Attendance/Approvals/ScheduleChangeApprovals';
import AllAttendanceRequests from './pages/Attendance/Approvals/AllAttendanceRequests';
import FaceReviewApprovals from './pages/Attendance/Approvals/FaceReviewApprovals';
import AttendanceCalculation from './pages/Attendance/Calculation/AttendanceCalculation';
import AttendanceReports from './pages/Attendance/Reports/AttendanceReports';

// Placeholder components with SideMenu support
const Organization = () => (
  <Box>
    <Outlet /> {/* Add this to render nested routes */}
  </Box>
);

const Processes = () => (
  <Box>
    <Outlet /> {/* Add this to render nested routes */}
  </Box>
);

const Attendance = () => (
  <Box sx={{ width: '100%' }}>
    <Outlet />
  </Box>
);

const System = () => (
  <Box sx={{ width: '100%' }}>
    <Outlet />
  </Box>
);

const Integrations = () => (
  <Box sx={{ width: '100%' }}>
    <Outlet />
  </Box>
);

// Create a custom theme

// RTL cache for Arabic layout
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });
const ltrCache = createCache({ key: 'mui' });

const theme = createTheme({
  palette: {
    primary: {
      main: '#2E65D4',
      light: '#EBF2FF',
      dark: '#1E54C0',
      contrastText: '#fff'
    },
    secondary: {
      main: '#F5F9FF',
    },
    background: {
      default: '#F5F9FF',
      paper: '#fff'
    },
    text: {
      primary: '#262626',
      secondary: '#595959',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontSize: '1.125rem',
      fontWeight: 500,
    },
    body2: {
      fontSize: '0.875rem',
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    // ── The dialog alignment axis (owner report 2026-08-08) ──────────────────
    // A dialog's title, body and actions must begin at ONE left edge. Before
    // this block the repo had no Dialog theme at all, so all 349 chrome
    // elements hand-wrote their inset and 204 of them set it in `sx`. The
    // result, measured off the owner's screenshots at 2x (dialog inner edge as
    // origin): title 16px (`p: 2`), field borders 24px (`p: 3`) — the title
    // aligned with nothing, in ~50 dialogs.
    //
    // The value and the reasoning live in theme/shellMetrics.ts next to the
    // shell's gutter, because it is the same class of defect one level down.
    //
    // Consequence to know: DialogActions' MUI default is 8px, so a site that
    // drops its `px: 3` depends on THIS block for the gutter.
    // `src/i18n/dialogAxis.test.ts` guards the pairing.
    //
    // Physical left/right rather than a logical property on purpose: the values
    // are symmetric, so the Arabic RTL cache has nothing to flip, and a
    // physical declaration cannot lose a specificity race with MUI's own
    // `padding` shorthand.
    MuiDialogTitle: {
      styleOverrides: {
        root: { paddingLeft: DIALOG_GUTTER, paddingRight: DIALOG_GUTTER },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { paddingLeft: DIALOG_GUTTER, paddingRight: DIALOG_GUTTER },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { paddingLeft: DIALOG_GUTTER, paddingRight: DIALOG_GUTTER },
      },
    },
    // ── Helper text sits on the box's edge, not 14px inside it ────────────────
    // Owner report 2026-08-09, four screenshots, after §17 had already lined the
    // title up with the field borders. Measured at 2x with the dialog's inner edge
    // as origin: title 30, field borders 30, value 59, and the helper under a field
    // ALSO 59 — the only element outside the boxes that was not on the shared axis.
    //
    // MUI's default is `marginLeft: 14` on the `contained` variant, which lines the
    // helper up with the VALUE it describes. That reasoning is defensible and it is
    // what the previous version of `shellMetrics.ts` asserted was correct. It is
    // still wrong for this product: the value is INSIDE a box and the helper is
    // OUTSIDE it, so the eye has no line to follow between them — what it compares
    // the helper to is the border it hangs under.
    //
    // Zero rather than `DIALOG_GUTTER`: the helper lives inside a `FormControl`
    // whose own left edge already IS the field's border, so 0 puts it there. Both
    // sides, so the Arabic RTL cache has nothing to flip.
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 0, marginRight: 0 },
      },
    },
  },
  shape: {
    borderRadius: 6,
  },
});

document.documentElement.style.setProperty('--primary-lighter', '#F5F9FF');

// ADR-030 — public Face Liveness capture page, lazy-loaded so the heavy
// Amplify liveness deps are code-split OUT of the main admin bundle (only the
// mobile WebView ever hits /liveness).
const LivenessCapture = lazy(() => import('./pages/Liveness/LivenessCapture'));

/**
 * AuthGuardedPublic — wraps public-only pages (e.g. Onboarding).
 * Redirects already-authenticated users to the role-correct landing
 * page (workers → /me/dashboard, admins → /dashboard).
 */
function AuthGuardedPublic({ children }: { children: React.ReactNode }) {
  const { authenticated, loading } = useAuth();
  return (
    <AuthGuardedPublicBoundary
      authenticated={authenticated}
      loading={loading}
      authenticatedFallback={<RoleRedirect />}
    >
      {children}
    </AuthGuardedPublicBoundary>
  );
}

/**
 * AuthRequiredOnly — for pages that need a valid token but NOT an
 * active org context (specifically: the OrgGate itself). Unauth'd
 * users bounce to /onboarding; otherwise render.
 */
function AuthRequiredOnly({ children }: { children: React.ReactNode }) {
  const { authenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      </Box>
    );
  }
  if (!authenticated) {
    saveReturnTo(location.pathname + location.search + location.hash);
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/** Redirects /auth/signup to the auth server's /signup page directly
 *  (skipping the /authorize → /sign-in default route, so users land
 *  on the signup form immediately). After signup completes the auth
 *  server redirects back to authorize → SSOCallback → dashboard. */
function SSOSignupRedirect() {
  const started = React.useRef(false);
  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    void redirectToSignup();
  }, []);
  return null;
}

/**
 * PrivateRoute — wraps protected routes.
 * Enforces two gates:
 *   1. Authenticated (valid token): if not, save the intended path and
 *      send the user to /onboarding (the pre-sign-in splash).
 *   2. Has an active organization context (localStorage
 *      `current_organization_id`): if not, save the intended path and
 *      send to /auth/select-org (the post-sign-in org picker / creator).
 *      Every business endpoint requires `X-Organization-Id`, so the
 *      app can't function without this bit.
 *
 * The axios interceptor in src/api/client.ts additionally self-heals a
 * stale org id (403 "Not a member of this organization") by clearing
 * it and redirecting here on its own. So stale caches recover even
 * mid-session, not just at nav boundaries.
 */
function PrivateRoute() {
  const { authenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  if (!authenticated) {
    saveReturnTo(location.pathname + location.search + location.hash);
    return <Navigate to="/onboarding" replace />;
  }

  if (!getCurrentOrgId()) {
    saveReturnTo(location.pathname + location.search + location.hash);
    return <Navigate to="/auth/select-org" replace />;
  }

  // If orgId exists but role_code is missing (stale localStorage from before
  // role-based routing was added), force re-select to populate it.
  const roleCode = getCurrentRoleCode();
  if (!roleCode) {
    saveReturnTo(location.pathname + location.search + location.hash);
    return <Navigate to="/auth/select-org" replace />;
  }

  return (
    <PortalBoundary roleCode={roleCode}>
      <Outlet />
    </PortalBoundary>
  );
}

/**
 * RoleRedirect — catch-all that sends workers to /me/dashboard
 * and admins to /dashboard based on stored role_code. Routing
 * decision is owned by ``useRoleLanding`` so adding a new role
 * (or changing the worker landing path) is a one-file change.
 */
function RoleRedirect() {
  const { landingPath } = useRoleLanding();
  return <Navigate to={landingPath} replace />;
}

function App() {
  // Per-product branding: set browser tab title and favicon based on
  // the build-time REACT_APP_PRODUCT (qutime or pureoa). Runs once on
  // mount; the values are baked into the bundle so SSR/hydration
  // mismatch isn't a concern.
  React.useEffect(() => {
    document.title = PRODUCT_NAME;
    const link =
      (document.querySelector("link[rel='icon']") as HTMLLinkElement | null) ??
      (() => {
        const l = document.createElement('link');
        l.rel = 'icon';
        document.head.appendChild(l);
        return l;
      })();
    link.href = FAVICON;
  }, []);

  const { i18n: i18nInstance, t } = useTranslation();
  const dynamicTheme = useMemo(() => createTheme(theme, {
    direction: i18nInstance.language === "ar" ? "rtl" : "ltr",
    components: {
      MuiTablePagination: {
        defaultProps: {
          labelRowsPerPage: t('common:tablePagination.rowsPerPage'),
          labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
            count !== -1
              ? t('common:tablePagination.displayedRows', { from, to, count })
              : t('common:tablePagination.displayedRowsMoreThan', { from, to }),
          getItemAriaLabel: (type: 'first' | 'last' | 'next' | 'previous') =>
            t('common:tablePagination.aria.' + type),
        },
      },
    },
  }), [i18nInstance.language, t]);

  // Date-picker locale — feeds <DateField> via <LocalizationProvider>
  // so the calendar UI matches the app's i18n language (Chrome's
  // native HTML5 picker reads browser UI lang, not <html lang>).
  const dateAdapterLocale = useMemo(() => {
    switch (i18nInstance.language) {
      case 'es': return esLocale;
      case 'pt': return pt;
      case 'fr': return fr;
      case 'de': return de;
      case 'ja': return ja;
      case 'zh-CN': return zhCN;
      case 'ar': return ar;
      case 'ko': return ko;
      default: return enUS;
    }
  }, [i18nInstance.language]);

  return (
    <CacheProvider value={i18nInstance.language === "ar" ? rtlCache : ltrCache}>
    <ThemeProvider theme={dynamicTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFnsV3} adapterLocale={dateAdapterLocale}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes — no authentication required */}
            {/* ADR-030 — Face Liveness capture, opened by the mobile app in a
                WebView. Fully public (auth lives in the mobile↔backend session
                mint/verify, not here). Lazy + Suspense so Amplify is code-split. */}
            <Route
              path="/liveness"
              element={
                <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: '#000' }} />}>
                  <LivenessCapture />
                </Suspense>
              }
            />
            {/* Onboarding gate: shown to unauthenticated users */}
            <Route
              path="/onboarding"
              element={
                // If already authenticated, skip onboarding and go straight to the app
                <AuthGuardedPublic><Onboarding /></AuthGuardedPublic>
              }
            />
            {/* SSO signup redirect shim */}
            <Route path="/auth/signup" element={<SSOSignupRedirect />} />
            {/* SSO callback — receives tokens from the backend after sign-in */}
            <Route path="/auth/callback" element={<SSOCallback />} />
            <Route path="/auth/sso-landing" element={<SSOLanding />} />

            {/* Post-sign-in org gate: picks / creates the active organization
                before the app shell loads. Requires auth but NOT an
                existing org context — that's what this page resolves. */}
            <Route
              path="/auth/select-org"
              element={
                <AuthRequiredOnly>
                  <OrgGate />
                </AuthRequiredOnly>
              }
            />

            {/* All application routes are protected by PrivateRoute */}
            <Route element={<PrivateRoute />}>
              {/* Printable A4 app sheet: outside the admin shell so the printout carries no navigation. */}
              <Route path="/print/app-poster" element={<AppPosterPage />} />
              <Route path="/" element={<MainLayout />}>
                <Route index element={<RoleRedirect />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="account/security" element={<AccountSecurity />} />
                <Route path="organization" element={<Organization />}>
                  <Route index element={<OrganizationProfile />} />
                  <Route path="profile" element={<OrganizationProfile />} />
                  <Route path="branches-locations" element={<BranchesLocations />} />
                  <Route path="departments" element={<Departments />} />
                  <Route path="positions" element={<Positions />} />
                  <Route path="employment-types" element={<EmploymentTypes />} />
                  <Route path="workers/documents" element={<WorkerDocuments />} />
                  <Route path="workers/active" element={<ActiveWorkers />} />
                  <Route path="workers/former" element={<FormerWorkers />} />
                  <Route path="worker-groups" element={<WorkerGroups />} />
                  {/* QuTime-only. The sidebar hides these two; a direct URL,
                      bookmark or history entry does not go through the sidebar,
                      which is what the boundary covers. It holds a spinner while
                      the licence is unknown, so a licensed admin's bookmark is
                      never bounced and an unlicensed one never sees the page. */}
                  <Route
                    path="workers/face-templates"
                    element={<AdminProductBoundary product="qutime"><FaceTemplates /></AdminProductBoundary>}
                  />
                  <Route path="chart" element={<OrganizationChart />} />
                  <Route path="events" element={<Events />} />
                  <Route path="announcements" element={<Announcements />} />
                </Route>
                <Route path="processes" element={<Processes />}>
                  <Route index element={<ProcessOverview />} />
                  <Route path="overview" element={<ProcessOverview />} />
                  <Route path="roles" element={<Roles />} />
                  <Route path="form-components" element={<FormComponents />} />
                  <Route path="nodes" element={<Navigate to="/processes/designer" replace />} />
                  {/* Editable Policy configuration catalog. Runtime execution rules for
                      active processes are configured in Process Designer. */}
                  <Route path="designer" element={<ProcessDesigner />} />
                  {/* One page; status is a filter. The four old URLs are still
                      linked from ProcessOverview's empty state and the setup guide,
                      so they redirect with the filter preserved rather than 404. */}
                  {/* ONE org-wide list (ADR-046). `requests/history` renders it; every older URL
                      redirects with its filter preserved. `instances` had its own nav entry and is
                      linked from the Setup Guide, ProcessOverview's Recent activity card and
                      notification deep links, so it redirects rather than 404s (svdy-ui §12.10). */}
                  <Route path="requests/history" element={<AllRequests />} />
                  <Route path="instances" element={<Navigate to="/processes/requests/history" replace />} />
                  <Route path="instances/active" element={<Navigate to="/processes/requests/history?status=active" replace />} />
                  <Route path="instances/completed" element={<Navigate to="/processes/requests/history?status=completed" replace />} />
                  <Route path="instances/failed" element={<Navigate to="/processes/requests/history?status=failed" replace />} />
                  <Route path="instances/monitoring" element={<Navigate to="/processes/requests/history" replace />} />
                  <Route path="instances/:id" element={<InstanceDetail />} />
                  <Route path="requests/pending" element={<PendingApprovals />} />
                  <Route path="builder" element={<Navigate to="/processes/designer" replace />} />
                  <Route path="analytics" element={<ProcessAnalytics />} />
                </Route>
                <Route path="attendance" element={<AdminProductBoundary product="qutime"><Attendance /></AdminProductBoundary>}>
                  <Route index element={<AttendanceOverview />} />
                  <Route path="overview" element={<AttendanceOverview />} />
                  <Route path="rules/global" element={<GlobalRules />} />
                  <Route path="rules/department" element={<DepartmentRules />} />
                  <Route path="rules/group" element={<GroupRules />} />
                  <Route path="shifts/breaks" element={<Breaks />} />
                  <Route path="shifts/timetables" element={<Timetables />} />
                  <Route path="shifts/cycles" element={<ShiftCycles />} />
                  <Route path="work-calendar" element={<WorkCalendar />} />
                  <Route path="schedules/view" element={<ScheduleView />} />
                  <Route path="schedules/department" element={<DepartmentSchedules />} />
                  <Route path="schedules/worker" element={<WorkerSchedules />} />
                  <Route path="schedules/temporary" element={<TemporarySchedules />} />
                  <Route path="leave/types" element={<LeaveTypes />} />
                  <Route path="leave/policies" element={<LeavePolicies />} />
                  <Route path="leave/public-holidays" element={<PublicHolidays />} />
                  <Route path="leave/balances" element={<LeaveBalances />} />
                  <Route path="geofences/department" element={<DepartmentGeofences />} />
                  <Route path="geofences/group" element={<GroupGeofences />} />
                  <Route path="geofences/worker" element={<WorkerGeofences />} />
                  <Route path="kiosk" element={<KioskDevices />} />
                  <Route path="time-entries" element={<TimeEntries />} />
                  <Route path="approvals" element={<AllAttendanceRequests />} />
                  <Route path="approvals/leave" element={<LeaveApprovals />} />
                  <Route path="approvals/overtime" element={<OvertimeApprovals />} />
                  <Route path="approvals/missed-clock-in" element={<MissedClockInApprovals />} />
                  <Route path="approvals/business-trip" element={<BusinessTripApprovals />} />
                  <Route path="approvals/off-site-work" element={<OffSiteWorkApprovals />} />
                  <Route path="approvals/schedule-change" element={<ScheduleChangeApprovals />} />
                  <Route
                    path="approvals/face-review"
                    element={<FaceReviewApprovals />}
                  />
                  <Route path="approvals/routing" element={<ApprovalRouting />} />
                  <Route path="calculation" element={<AttendanceCalculation />} />
                  <Route path="reports" element={<AttendanceReports />} />
                </Route>
                <Route path="integrations" element={<Integrations />}>
                  <Route index element={<IntegrationsOverview />} />
                  <Route path="overview" element={<IntegrationsOverview />} />
                  <Route path="connectors" element={<IntegrationsConnectors />} />
                  <Route path="api" element={<IntegrationsAPI />} />
                  <Route path="webhooks" element={<IntegrationsWebhooks />} />
                </Route>
                <Route path="system" element={<System />}>
                  <Route index element={<SystemOverview />} />
                  <Route path="overview" element={<SystemOverview />} />
                  <Route path="access-control/roles" element={<RolesPermissions />} />
                  <Route path="access-control/groups" element={<Navigate to="/organization/worker-groups" replace />} />
                  <Route path="audit-logs/access" element={<AccessLogs />} />
                  <Route path="audit-logs/activity" element={<ActivityLogs />} />
                  <Route path="settings" element={<SettingsLanding />} />
                  <Route path="settings/security" element={<SecuritySettings />} />
                  <Route path="settings/workers" element={<WorkersSettings />} />
                  <Route path="settings/notifications" element={<NotificationSettings />} />
                  <Route path="settings/sso" element={<SsoSettings />} />
                  <Route path="settings/sso/strict-mode" element={<SsoStrictMode />} />
                  <Route path="settings/data-export" element={<DataExportSettings />} />
                  <Route path="devices" element={<Devices />} />
                  <Route path="billing" element={<SystemBilling />} />
                  <Route path="support" element={<SystemSupport />} />
                </Route>
              </Route>
            </Route>

            {/* Worker Portal routes — role=worker gets this layout */}
            <Route element={<PrivateRoute />}>
              <Route path="/me" element={<WorkerLayout />}>
                <Route index element={<Navigate to="/me/dashboard" replace />} />
                {workerRoute('dashboard', <WorkerDashboard />)}
                {workerRoute('schedule', <WorkerSchedule />)}
                {workerRoute('time-entries', <WorkerTimeEntries />)}
                {/* These three carried NO product boundary until an audit of the licence gating
                    compared all three layers (2026-09-04, owner: 这个要审计一下，很重要).
                    The backend already fails closed on them — `ME_ROUTE_LICENSE_REQUIREMENTS` maps
                    `/me/tasks` and `/me/requests` (prefix, so `/new` and `/{id}` too) to pureoa — so
                    this was never a licence BYPASS. What an unlicensed worker got by typing the URL
                    was a page that renders and then errors on its first request, instead of the clean
                    redirect the boundary performs. Consistency and error quality, not security. */}
                {workerRoute('tasks', <WorkerTasks />)}
                {workerRoute('leave', <WorkerLeave />)}
                {workerRoute('holidays', <WorkerHolidays />)}
                {workerRoute('requests', <WorkerRequests />)}
                {workerRoute('requests/new', <WorkerSubmitRequest />)}
                {workerRoute('requests/:id', <WorkerRequestDetail />)}
                {workerRoute('team', <WorkerTeam />)}
                {workerRoute('approvals', <WorkerApprovals />)}
                {workerRoute('organization', <WorkerOrganization />)}
                {workerRoute('org-chart', <WorkerOrgChart />)}
                {workerRoute('profile', <WorkerProfile />)}
                {workerRoute('security', <AccountSecurity />)}
                {/* QuTime-only, and the reason is stronger here than in the admin
                    shell: both screens ask a worker to submit or review biometric
                    data (GDPR Art. 9), which a PureOA-only employer has no lawful
                    basis to process. Unknown licence holds a spinner, never the
                    page. */}
                {workerRoute('face-template', <WorkerFaceTemplate />)}
                {workerRoute('clock-in-reviews', <WorkerClockInReviews />)}
                {/* Merged into the single "My requests" hub (owner 2026-09-03, option A),
                    exactly as attendance-approvals was merged into /me/approvals in June.
                    Old deep links, notification links and bookmarks redirect rather than
                    404 — svdy-ui §12.10: a route that exists but is unreachable from where
                    you link it is the same defect as one that never existed. */}
                {workerRoute('attendance-requests', <WorkerAttendanceRequests />)}
                {/* Unified into the single Approvals hub (owner 2026-06-28
                    option A). Old deep-links / notifications redirect there. */}
                {workerRoute('attendance-approvals', <WorkerAttendanceApprovals />)}
                {/* Face Approvals — the SAME component the Attendance module mounts (imported above),
                    not a copy. It is a props-less React.FC over the shared /v1/attendance/approvals
                    endpoint with no admin-only chrome, so the worker-portal reviewer (role `manager`,
                    which stays in this shell) gets the queue their phone already had. A second
                    implementation would be exactly the drift svdy-rules §10.5 exists to prevent. */}
                {workerRoute('face-reviews', <FaceReviewApprovals />)}
              </Route>
            </Route>

            {/* Catch-all: redirect based on role */}
            <Route path="*" element={<RoleRedirect />} />
          </Routes>
        </Router>
        {/* Step-up re-authentication (ADR-014 D6).
         *
         * MOUNTED HERE AND NOT INSIDE A SHELL, on purpose. Until 2026-07-31 this
         * component was never rendered ANYWHERE: `grep -rn "<StepUpModal" src`
         * returned 0. It registered itself with the module-level coordinator on
         * mount, so with no mount there was no opener, and `requestStepUp()` took
         * its fail-fast branch and rejected every time. That made 2FA enrolment
         * impossible on every surface that needs it — /account/security,
         * /me/security, and both Enforce2FAGate prompts — because `auth_time` is
         * stamped only at sign-in and inherited by refresh (OIDC), so any
         * enrolment attempt more than 5 minutes after signing in MUST step up.
         *
         * A missing render is invisible to TypeScript, to the build, and to every
         * test that does not assert reachability, which is why it survived. It is
         * outside <Routes> so it cannot be unmounted by navigation, and above both
         * shells so no future shell can forget it. `stepUpModalMounted.test.ts`
         * fails if this line is removed. */}
        <StepUpModal />
      </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
    </CacheProvider>
  );
}

export default App;